/**
 * Precious materials (Foundry: item/physical/materials.ts at the pinned release, ADR-0004): for
 * weapons, armour, shields (bucklers and tower shields apart) and other objects, the level, price
 * in gold, rarity, and for shields the hardness and Hit Points of each grade. Foundry keeps these
 * in its source rather than in a Pack, so the app carries the same tables.
 *
 * Bulk changes are the printed rule (GM Core, "Dawnsilver" and "Duskwood"): a metal item of
 * dawnsilver or any item of duskwood is one Bulk lighter (1 to light, light unchanged). Foundry
 * does not apply it; the app does.
 */
export type MaterialGrade = "low" | "standard" | "high";
export const MATERIAL_GRADES: readonly MaterialGrade[] = ["low", "standard", "high"];

export interface MaterialGradeData {
  level: number;
  /** Gold pieces. */
  price: number;
  hardness?: number;
  maxHP?: number;
  rarity: string;
}

export type MaterialValuation = Partial<Record<string, Partial<Record<MaterialGrade, MaterialGradeData | null>>>>;

export const PRECIOUS_MATERIAL_TYPES = [
  "abysium", "adamantine", "cold-iron", "duskwood", "djezet", "dragonhide", "dreamweb", "grisantian-pelt", "inubrix", "keep-stone",
  "dawnsilver", "noqual", "orichalcum", "peachwood", "siccatite", "silver", "sisterstone", "sisterstone-dusk", "sisterstone-scarlet",
  "sloughstone", "sovereign-steel", "throneglass", "warpglass",
] as const;
export type PreciousMaterialType = (typeof PRECIOUS_MATERIAL_TYPES)[number];

export function isPreciousMaterial(value: unknown): value is PreciousMaterialType {
  return typeof value === "string" && (PRECIOUS_MATERIAL_TYPES as readonly string[]).includes(value);
}

export function isMaterialGrade(value: unknown): value is MaterialGrade {
  return typeof value === "string" && (MATERIAL_GRADES as readonly string[]).includes(value);
}

const g = (level: number, price: number, rarity: string, hardness?: number, maxHP?: number): MaterialGradeData =>
  hardness === undefined ? { level, price, rarity } : { level, price, rarity, hardness, maxHP: maxHP ?? 0 };

const WEAPON: MaterialValuation = {
  abysium: { standard: g(12, 2000, "rare"), high: g(18, 24000, "rare") },
  adamantine: { standard: g(11, 1400, "uncommon"), high: g(17, 13500, "uncommon") },
  "cold-iron": { low: g(2, 40, "common"), standard: g(10, 880, "common"), high: g(16, 9000, "common") },
  dawnsilver: { standard: g(11, 1400, "uncommon"), high: g(17, 13500, "uncommon") },
  djezet: { standard: g(12, 1800, "rare"), high: g(18, 22000, "rare") },
  duskwood: { standard: g(11, 1400, "uncommon"), high: g(17, 13500, "uncommon") },
  inubrix: { standard: g(11, 1400, "rare"), high: g(17, 13500, "rare") },
  "keep-stone": { high: g(18, 22500, "rare") },
  noqual: { standard: g(12, 1600, "rare"), high: g(18, 24000, "rare") },
  peachwood: { standard: g(12, 2000, "uncommon"), high: g(18, 19000, "uncommon") },
  orichalcum: { high: g(18, 22500, "rare") },
  siccatite: { standard: g(11, 1400, "rare"), high: g(17, 15000, "rare") },
  silver: { low: g(2, 40, "common"), standard: g(10, 880, "common"), high: g(16, 9000, "common") },
  "sisterstone-dusk": { low: g(3, 70, "rare"), standard: g(11, 1200, "rare"), high: g(19, 32000, "rare") },
  "sisterstone-scarlet": { low: g(3, 70, "rare"), standard: g(11, 1200, "rare"), high: g(19, 32000, "rare") },
  sloughstone: { standard: g(8, 3500, "rare"), high: g(16, 6000, "rare") },
  "sovereign-steel": { standard: g(12, 1600, "rare"), high: g(19, 32000, "rare") },
  throneglass: { low: g(2, 20, "rare"), standard: g(8, 350, "rare"), high: g(16, 6000, "rare") },
  warpglass: { high: g(17, 14000, "rare") },
};

const ARMOR: MaterialValuation = {
  abysium: { standard: g(12, 2000, "rare"), high: g(19, 40000, "rare") },
  adamantine: { standard: g(12, 1600, "uncommon"), high: g(19, 32000, "uncommon") },
  "cold-iron": { low: g(5, 140, "common"), standard: g(11, 1200, "common"), high: g(18, 20000, "common") },
  dawnsilver: { standard: g(12, 1600, "uncommon"), high: g(19, 32000, "uncommon") },
  djezet: { standard: g(12, 1800, "rare"), high: g(19, 35000, "rare") },
  dragonhide: { standard: g(12, 1600, "uncommon"), high: g(19, 32000, "uncommon") },
  dreamweb: { standard: g(5, 150, "rare"), high: g(14, 3000, "rare") },
  duskwood: { standard: g(12, 1600, "uncommon"), high: g(19, 32000, "uncommon") },
  "grisantian-pelt": { standard: g(12, 1800, "rare"), high: g(19, 33000, "rare") },
  inubrix: { standard: g(11, 1200, "rare"), high: g(18, 18000, "rare") },
  "keep-stone": { high: g(20, 56000, "rare") },
  noqual: { standard: g(12, 1600, "rare"), high: g(19, 32000, "rare") },
  orichalcum: { high: g(20, 55000, "rare") },
  siccatite: { standard: g(12, 1600, "rare"), high: g(19, 32000, "rare") },
  silver: { low: g(5, 140, "common"), standard: g(11, 1200, "common"), high: g(18, 20000, "common") },
  "sisterstone-dusk": { low: g(5, 140, "rare"), standard: g(10, 1000, "rare"), high: g(18, 19500, "rare") },
  "sisterstone-scarlet": { low: g(5, 140, "rare"), standard: g(10, 1000, "rare"), high: g(18, 19500, "rare") },
  "sovereign-steel": { standard: g(13, 2400, "rare"), high: g(20, 50000, "rare") },
};

const OBJECT: MaterialValuation = {
  abysium: { standard: g(8, 450, "rare"), high: g(16, 7500, "rare") },
  adamantine: { standard: g(8, 350, "uncommon"), high: g(16, 6000, "uncommon") },
  "cold-iron": { low: g(2, 20, "common"), standard: g(7, 250, "common"), high: g(15, 4500, "common") },
  dawnsilver: { standard: g(8, 350, "uncommon"), high: g(16, 6000, "uncommon") },
  djezet: { standard: g(8, 400, "rare"), high: g(16, 7000, "rare") },
  duskwood: { standard: g(8, 350, "uncommon"), high: g(16, 6000, "uncommon") },
  inubrix: { standard: g(8, 400, "rare"), high: g(16, 1600, "rare") },
  "keep-stone": { high: g(17, 9000, "rare") },
  noqual: { standard: g(8, 400, "rare"), high: g(16, 7000, "rare") },
  orichalcum: { high: g(17, 10000, "rare") },
  peachwood: { standard: g(8, 500, "uncommon"), high: g(16, 7500, "uncommon") },
  siccatite: { standard: g(8, 350, "rare"), high: g(16, 6000, "rare") },
  silver: { low: g(2, 20, "common"), standard: g(7, 250, "common"), high: g(15, 4500, "common") },
  sisterstone: { low: g(3, 30, "rare"), standard: g(8, 350, "rare"), high: g(16, 5500, "rare") },
  "sovereign-steel": { standard: g(9, 500, "rare"), high: g(17, 8000, "rare") },
  throneglass: { low: g(2, 20, "rare"), standard: g(8, 350, "rare"), high: g(16, 6000, "rare") },
  warpglass: { high: g(17, 8000, "rare") },
};

const BUCKLER: MaterialValuation = {
  abysium: { standard: g(8, 400, "rare", 4, 16), high: g(16, 8000, "rare", 7, 28) },
  adamantine: { standard: g(8, 400, "uncommon", 8, 32), high: g(16, 8000, "uncommon", 11, 44) },
  "cold-iron": { low: g(2, 30, "common", 3, 12), standard: g(7, 300, "common", 5, 20), high: g(15, 5000, "common", 8, 32) },
  dawnsilver: { standard: g(8, 400, "uncommon", 3, 12), high: g(16, 8000, "uncommon", 6, 24) },
  djezet: { standard: g(9, 600, "rare", 3, 12), high: g(16, 8000, "rare", 6, 24) },
  duskwood: { standard: g(8, 400, "uncommon", 3, 12), high: g(16, 8000, "uncommon", 5, 20) },
  inubrix: { standard: g(7, 320, "rare", 2, 8), high: g(15, 5000, "rare", 5, 20) },
  noqual: { high: g(17, 14000, "rare", 7, 28) },
  orichalcum: { high: g(17, 12000, "rare", 14, 56) },
  siccatite: { standard: g(8, 400, "rare", 4, 16), high: g(16, 8000, "rare", 7, 28) },
  silver: { low: g(2, 30, "common", 1, 4), standard: g(7, 300, "common", 3, 12), high: g(15, 5000, "common", 6, 24) },
};

const SHIELD: MaterialValuation = {
  abysium: { standard: g(8, 440, "rare", 6, 24), high: g(16, 8800, "rare", 10, 40) },
  adamantine: { standard: g(8, 440, "uncommon", 10, 40), high: g(16, 8800, "uncommon", 13, 52) },
  "cold-iron": { low: g(2, 34, "common", 5, 20), standard: g(7, 340, "common", 7, 28), high: g(15, 5500, "common", 10, 40) },
  dawnsilver: { standard: g(8, 440, "uncommon", 5, 20), high: g(16, 8800, "uncommon", 8, 32) },
  djezet: { standard: g(9, 660, "rare", 5, 20), high: g(16, 8800, "rare", 8, 32) },
  duskwood: { standard: g(8, 440, "uncommon", 5, 20), high: g(16, 8800, "uncommon", 8, 32) },
  inubrix: { standard: g(7, 352, "rare", 4, 16), high: g(15, 5500, "rare", 7, 28) },
  "keep-stone": { high: g(17, 13200, "rare", 11, 46) },
  noqual: { high: g(17, 15400, "rare", 10, 40) },
  orichalcum: { high: g(17, 13200, "rare", 16, 64) },
  siccatite: { standard: g(8, 440, "rare", 6, 24), high: g(16, 8800, "rare", 10, 40) },
  silver: { low: g(2, 34, "common", 3, 12), standard: g(7, 340, "common", 5, 20), high: g(15, 5500, "common", 8, 32) },
};

const TOWER_SHIELD: MaterialValuation = {
  duskwood: { standard: g(8, 560, "uncommon", 5, 20), high: g(16, 11200, "uncommon", 8, 32) },
};

export const MATERIAL_DATA = { armor: ARMOR, object: OBJECT, shield: { shield: SHIELD, buckler: BUCKLER, towerShield: TOWER_SHIELD }, weapon: WEAPON } as const;

export const BUCKLER_BASE_TYPES = ["buckler", "casters-targe", "dart-shield", "gauntlet-buckler", "heavy-rondache", "klar"] as const;
export const TOWER_SHIELD_BASE_TYPES = ["fortress-shield", "tower-shield"] as const;

export interface MaterialLike {
  type: string;
  baseType: string | null;
  material: { type: string | null; grade: string | null };
}

/** The valuation table an item's material reads: weapons, armour, and shields by kind; anything else has none. */
export function materialTableFor(item: Pick<MaterialLike, "type" | "baseType">): MaterialValuation | null {
  switch (item.type) {
    case "weapon": return MATERIAL_DATA.weapon;
    case "armor": return MATERIAL_DATA.armor;
    case "shield":
      return (BUCKLER_BASE_TYPES as readonly string[]).includes(item.baseType ?? "") ? MATERIAL_DATA.shield.buckler : (TOWER_SHIELD_BASE_TYPES as readonly string[]).includes(item.baseType ?? "") ? MATERIAL_DATA.shield.towerShield : MATERIAL_DATA.shield.shield;
    default: return null;
  }
}

/** Foundry's `getMaterialValuationData`: the level, price and rarity of the item's material and grade, or null. */
export function materialValuation(item: MaterialLike): MaterialGradeData | null {
  const { type, grade } = item.material;
  if (!type || !grade) return null;
  return materialTableFor(item)?.[type]?.[grade as MaterialGrade] ?? null;
}

/** The grades a material comes in for an item type, from the table (an empty list when the material does not fit the type). */
export function materialGradesFor(item: Pick<MaterialLike, "type" | "baseType">, material: string): MaterialGrade[] {
  const table = materialTableFor(item);
  const row = table?.[material];
  if (!row) return [];
  return MATERIAL_GRADES.filter((grade) => !!row[grade]);
}

/** Materials the table lists for an item type. */
export function materialsFor(item: Pick<MaterialLike, "type" | "baseType">): string[] {
  const table = materialTableFor(item);
  return table ? Object.keys(table).filter((m) => materialGradesFor(item, m).length > 0) : [];
}

/** Materials that are metals: dawnsilver lightens only these (GM Core). */
export const METAL_MATERIALS: readonly string[] = ["abysium", "adamantine", "cold-iron", "dawnsilver", "djezet", "inubrix", "noqual", "orichalcum", "siccatite", "silver", "sovereign-steel"];

/** Whether the printed rule lightens an item of this material by one Bulk step. */
export function materialLightens(material: string | null): boolean {
  return material === "dawnsilver" || material === "duskwood";
}
